import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  MapPin,
  Plus,
  Truck,
  ChevronDown,
  Users,
  QrCode,
  Banknote,
  Gift,
  ShieldCheck,
  CreditCard,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import {
  publicCheckout,
  createSquarePaymentLink,
  createStripeSession,
  fetchMyTeam,
  fetchCommissionBalance,
  saveProfile,
  getProfile,
} from "@/lib/api";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const ZELLE_HANDLE = "202-500-1162";
const VENMO_HANDLE = "@OreVital";

/**
 * Two-step Pay Later / Square / Invoice Me / Zelle-Venmo checkout flow.
 *
 * Step 1 — Select shipping address (saved profile address, edit inline,
 *          copy from billing, OR drop-ship to a CRM contact).
 * Step 2 — Choose payment method:
 *           • Pay Later   – invoice routed through Postcue (existing)
 *           • Square QR   – `/postcue/checkout/square-link` returns hosted
 *                           checkout URL, rendered as QR
 *           • Invoice Me  – create the order, dealer follows up via Square
 *                           invoice (same path as Pay Later for now)
 *           • Zelle/Venmo – 5% off auto-applied, customer pays out-of-band
 *
 * Free BOGO rows pass through with price=0 so Airtable + ShipStation see
 * the correct line-item count.
 */
export default function CheckoutFlow({
  user,
  dealerSlug,
  lines,
  subRole,
  shipping,
  tax,
  total: cartTotal,
  couponCode,
  onClose,
  onSuccess,
}) {
  const [step, setStep] = useState(1); // 1 = address, 2 = payment, 3 = done
  const [busy, setBusy] = useState(false);

  // ─── Step 1 state ───────────────────────────────────────────────────────
  const [shipMode, setShipMode] = useState("ship_to_me"); // | "drop_ship"
  const [address, setAddress] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email: user?.email_id || user?.email || "",
    phone: user?.phone_no || user?.phone || "",
    address: user?.address || "",
    address2: user?.address2 || "",
    city: user?.city || "",
    state: user?.state || "",
    zip: user?.postcode || user?.zip || "",
    country: user?.country || "US",
  });

  // Drop-ship contacts (dealer's CRM team)
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState("");
  // Shipping address (collapsible). Defaults to "same as billing".
  const [shipOpen, setShipOpen] = useState(false);
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [shippingAddress, setShippingAddress] = useState({
    address: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
  });
  const [savingAddr, setSavingAddr] = useState(false);

  // Two-way sync: pull the dealer's saved profile address and pre-fill the
  // checkout (Order→Profile already saves; this completes Profile→Order).
  useEffect(() => {
    const recId = user?.user_record_id || user?.record_id || user?.id;
    if (!recId || shipMode === "drop_ship") return;
    let active = true;
    getProfile(recId)
      .then((res) => {
        const p = res?.profile || {};
        if (!active) return;
        setAddress((a) => ({
          ...a,
          first_name: a.first_name || p.first_name || "",
          last_name: a.last_name || p.last_name || "",
          email: a.email || p.email_id || p.email || "",
          phone: a.phone || p.phone_no || "",
          address: a.address || p.address || "",
          address2: a.address2 || p.address2 || "",
          city: a.city || p.city || "",
          state: a.state || p.state || "",
          zip: a.zip || p.postcode || p.zip || "",
          country: a.country || p.country || "US",
        }));
        const sameBill =
          p.ship_same_as_billing === 1 ||
          (p.ship_same_as_billing !== 0 && !p.ship_address);
        setSameAsBilling(sameBill);
        if (!sameBill && p.ship_address) {
          setShippingAddress({
            address: p.ship_address || "",
            address2: p.ship_address2 || "",
            city: p.ship_city || "",
            state: p.ship_state || "",
            zip: p.ship_postcode || p.ship_zip || "",
            country: p.ship_country || "US",
          });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [user, shipMode]);

  const saveAddress = async () => {
    const recId = user?.user_record_id || user?.record_id || user?.id;
    if (!recId) {
      toast.error("Sign in to save your address");
      return;
    }
    if (!address.address || !address.city || !address.state || !address.zip) {
      toast.error("Fill the billing address first");
      return;
    }
    setSavingAddr(true);
    try {
      const ship = sameAsBilling ? address : shippingAddress;
      await saveProfile({
        user_record_id: recId,
        first_name: address.first_name || "",
        last_name: address.last_name || "",
        address: address.address,
        address2: address.address2 || "",
        city: address.city,
        state: address.state,
        postcode: address.zip,
        country: address.country || "US",
        ship_address: ship.address || "",
        ship_address2: ship.address2 || "",
        ship_city: ship.city || "",
        ship_state: ship.state || "",
        ship_postcode: ship.zip || "",
        ship_country: ship.country || "US",
        ship_same_as_billing: sameAsBilling ? 1 : 0,
      });
      toast.success("Address saved to your profile");
    } catch {
      toast.error("Could not save address");
    } finally {
      setSavingAddr(false);
    }
  };

  useEffect(() => {
    if (!user || shipMode !== "drop_ship" || contacts.length > 0) return;
    setContactsLoading(true);
    fetchMyTeam(user)
      .then((d) => setContacts(Array.isArray(d?.team_members) ? d.team_members : []))
      .catch(() => {
        /* dealer with no team — keep contacts empty */
      })
      .finally(() => setContactsLoading(false));
  }, [shipMode, user, contacts.length]);

  function pickContact(c) {
    setSelectedContactId(c.id);
    // Replace the address with the contact's own details. Never fall back to
    // the dealer's (Laurie's) address, and NEVER carry the customer's email —
    // ambassador drop-ship orders must not capture the customer's email.
    setAddress((a) => ({
      ...a,
      first_name: c.first_name || "",
      last_name: c.last_name || "",
      email: "",
      phone: c.phone || "",
      address: c.address || "",
      address2: c.address2 || "",
      city: c.city || "",
      state: c.state || "",
      zip: c.postcode || c.zip || "",
      country: c.country || "US",
    }));
  }

  // ─── Step 2 state ───────────────────────────────────────────────────────
  const [method, setMethod] = useState("stripe_card");
  const [notes, setNotes] = useState("");
  const [orderResult, setOrderResult] = useState(null);
  const [squareUrl, setSquareUrl] = useState("");

  // ─── Commission redemption ──────────────────────────────────────────────
  const [commAvailable, setCommAvailable] = useState(0);
  const [useCommission, setUseCommission] = useState(false);
  const [applyInput, setApplyInput] = useState("");

  // ─── Rewards / Points redemption ────────────────────────────────────────
  const [rewardsAvailable, setRewardsAvailable] = useState(0);
  const [useRewards, setUseRewards] = useState(false);
  const [rewardsInput, setRewardsInput] = useState("");

  useEffect(() => {
    const rec = user?.user_record_id || user?.record_id || user?.id;
    if (!rec) return;
    let active = true;
    fetchCommissionBalance(rec)
      .then((d) => {
        if (!active) return;
        // Only update commission from a row that actually exists — never
        // clobber a good balance with 0 from a transient Airtable read error.
        if (d?.commission_row_id || Number(d?.available) > 0) {
          setCommAvailable(Math.max(0, Number(d?.available || 0)));
        }
        // Rewards balance (Users.Points) rides on the same response.
        setRewardsAvailable(Math.max(0, Number(d?.points || 0)));
      })
      .catch(() => {
        /* keep last known balance */
      });
    return () => {
      active = false;
    };
  }, [user?.user_record_id, user?.record_id, user?.id]);

  const discountAmt = useMemo(() => {
    if (method === "zelle" || method === "venmo") {
      return Math.round(subRole * 0.05 * 100) / 100;
    }
    return 0;
  }, [method, subRole]);
  // Amount payable before any credit (cart minus any payment-method discount)
  const payableBeforeRewards = useMemo(
    () => Math.max(0, Math.round((cartTotal - discountAmt) * 100) / 100),
    [cartTotal, discountAmt]
  );
  // Rewards applied first = min(requested, available, payable)
  const rewardsApplied = useMemo(() => {
    if (!useRewards || rewardsAvailable <= 0) return 0;
    const requested = rewardsInput === "" ? rewardsAvailable : Number(rewardsInput) || 0;
    return Math.max(
      0,
      Math.round(Math.min(requested, rewardsAvailable, payableBeforeRewards) * 100) / 100
    );
  }, [useRewards, rewardsAvailable, rewardsInput, payableBeforeRewards]);
  // Amount payable before commission (after rewards)
  const payableBeforeCommission = useMemo(
    () => Math.max(0, Math.round((payableBeforeRewards - rewardsApplied) * 100) / 100),
    [payableBeforeRewards, rewardsApplied]
  );
  // Commission actually applied = min(requested, available, payable)
  const commissionApplied = useMemo(() => {
    if (!useCommission || commAvailable <= 0) return 0;
    const requested = applyInput === "" ? commAvailable : Number(applyInput) || 0;
    return Math.max(
      0,
      Math.round(Math.min(requested, commAvailable, payableBeforeCommission) * 100) / 100
    );
  }, [useCommission, commAvailable, applyInput, payableBeforeCommission]);
  const finalTotal = useMemo(
    () =>
      Math.max(0, Math.round((payableBeforeCommission - commissionApplied) * 100) / 100),
    [payableBeforeCommission, commissionApplied]
  );
  const fullyCovered =
    (commissionApplied > 0 || rewardsApplied > 0) && finalTotal <= 0;

  // ─── Submit ─────────────────────────────────────────────────────────────
  async function submit() {
    if (!user) {
      toast.error("Please sign in to checkout");
      return;
    }
    if (!address.address || !address.city || !address.state || !address.zip) {
      toast.error("Please fill the billing address");
      setStep(1);
      return;
    }
    setBusy(true);
    try {
      const itemsPayload = lines.map((l) => ({
        product_record_id: l.id,
        qty: l.qty,
        price: l.role_price,
        ...(l.kind === "free"
          ? { note: `BOGO free (${l.promo?.label || "promo"})` }
          : {}),
      }));
      // Drop-ship vs ship-to-me: when drop-shipping, the buyer record stays
      // the signed-in user but the shipping block points at the contact.
      const isDropShip = shipMode === "drop_ship" && selectedContactId;
      const customer = isDropShip
        ? {
            first_name: user.first_name || "",
            last_name: user.last_name || "",
            email: user.email_id || user.email || "",
            phone: user.phone_no || user.phone || "",
            address: user.address || "",
            address2: user.address2 || "",
            city: user.city || "",
            state: user.state || "",
            zip: user.postcode || user.zip || "",
            country: user.country || "US",
          }
        : address;
      const shipTo = isDropShip
        ? address
        : shipMode === "ship_to_me" && !sameAsBilling && shippingAddress.address
          ? {
              first_name: address.first_name,
              last_name: address.last_name,
              address: shippingAddress.address,
              address2: shippingAddress.address2,
              city: shippingAddress.city,
              state: shippingAddress.state,
              zip: shippingAddress.zip,
              country: shippingAddress.country,
            }
          : null;
      const discountReason =
        method === "zelle"
          ? "Zelle 5% off"
          : method === "venmo"
            ? "Venmo 5% off"
            : "";

      const res = await publicCheckout({
        dealerSlug,
        customer,
        items: itemsPayload,
        shipping_charge: shipping,
        tax,
        coupon_code: couponCode || "",
        payment_method: method,
        ship_to: shipTo,
        discount: discountAmt,
        discount_reason: discountReason,
        rewards_redeemed: rewardsApplied,
        commission_redeemed: commissionApplied,
        notes: notes || "",
      });
      setOrderResult(res);

      // Stripe hosted card checkout — redirect the buyer to Stripe. Skipped when
      // commission fully covers the order (nothing left to pay).
      if (method === "stripe_card" && !fullyCovered) {
        try {
          const s = await createStripeSession({
            orderRecordId: res.order_id,
            originUrl: window.location.origin,
            returnPath: window.location.pathname,
          });
          if (s?.url) {
            window.location.href = s.url;
            return;
          }
          throw new Error("No checkout URL received");
        } catch (e) {
          toast.error(
            e?.response?.data?.detail || "Could not start card payment"
          );
          return;
        }
      }

      // Square QR — create the hosted checkout link and display it.
      // Skipped when commission fully covers the order (nothing left to pay).
      if (method === "square_scan" && !fullyCovered) {
        const itemsCents = lines.map((l) => ({
          name: l.name + (l.kind === "free" ? " (FREE)" : ""),
          qty: l.qty,
          price_cents: Math.round(Number(l.role_price || 0) * 100),
        }));
        const totalCents = Math.round(finalTotal * 100);
        const sq = await createSquarePaymentLink({
          orderRecordId: res.order_id,
          orderNumber: String(res.order_number || ""),
          totalCents,
          items: itemsCents,
          buyerEmail: customer.email,
        });
        setSquareUrl(sq.url || sq.long_url || "");
      }

      onSuccess?.(res);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Checkout failed");
    } finally {
      setBusy(false);
    }
  }

  // ─── Step indicator ────────────────────────────────────────────────────
  const Steps = (
    <div className="flex items-center gap-2 px-1 mb-3 text-[11px] font-semibold uppercase tracking-wide">
      {[
        { n: 1, label: "Address" },
        { n: 2, label: "Payment" },
        { n: 3, label: "Done" },
      ].map((s, i) => (
        <div key={s.n} className="flex items-center gap-1.5">
          <span
            className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
              step >= s.n
                ? "bg-emerald-600 text-white"
                : "bg-zinc-200 text-zinc-500"
            }`}
          >
            {step > s.n ? "✓" : s.n}
          </span>
          <span
            className={step >= s.n ? "text-zinc-800" : "text-zinc-400"}
          >
            {s.label}
          </span>
          {i < 2 && (
            <span className="mx-1 text-zinc-300">›</span>
          )}
        </div>
      ))}
    </div>
  );

  // ═══ STEP 1 — Address ═════════════════════════════════════════════════
  if (step === 1) {
    return (
      <div className="px-5 py-4 space-y-4" data-testid="checkout-step-1">
        {Steps}

        <h2 className="text-base font-bold text-zinc-900 flex items-center gap-2">
          <MapPin size={16} className="text-emerald-600" />
          Where should we ship this?
        </h2>

        {/* Mode toggle */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setShipMode("ship_to_me")}
            className={`rounded-xl border p-3 text-left transition ${
              shipMode === "ship_to_me"
                ? "border-emerald-500 bg-emerald-50/40 ring-1 ring-emerald-300"
                : "border-zinc-200 hover:border-zinc-300"
            }`}
            data-testid="ship-mode-me"
          >
            <div className="flex items-center gap-2">
              <Truck size={14} className="text-emerald-600" />
              <span className="text-sm font-semibold">Ship to me</span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              Your saved address
            </div>
          </button>
          <button
            onClick={() => setShipMode("drop_ship")}
            className={`rounded-xl border p-3 text-left transition ${
              shipMode === "drop_ship"
                ? "border-violet-500 bg-violet-50/40 ring-1 ring-violet-300"
                : "border-zinc-200 hover:border-zinc-300"
            }`}
            data-testid="ship-mode-drop"
          >
            <div className="flex items-center gap-2">
              <Users size={14} className="text-violet-600" />
              <span className="text-sm font-semibold">Drop-ship</span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              Send to a contact
            </div>
          </button>
        </div>

        {/* Drop-ship contact picker */}
        {shipMode === "drop_ship" && (
          <div className="rounded-2xl border border-violet-200 bg-violet-50/30 p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wide font-bold text-violet-700">
              Pick a contact to ship to
            </div>
            {contactsLoading ? (
              <div className="py-3 flex items-center justify-center text-violet-700 text-sm">
                <Loader2 className="animate-spin mr-1.5" size={13} /> Loading
                contacts…
              </div>
            ) : contacts.length === 0 ? (
              <div className="text-xs text-zinc-500 py-2">
                No CRM contacts yet — switch to <b>Ship to me</b>.
              </div>
            ) : (
              <ul className="max-h-44 overflow-y-auto divide-y divide-violet-100 bg-white rounded-lg border border-violet-100">
                {contacts.map((c) => {
                  const name =
                    c.full_name ||
                    `${c.first_name || ""} ${c.last_name || ""}`.trim() ||
                    c.email ||
                    "Contact";
                  const sel = selectedContactId === c.id;
                  return (
                    <li key={c.id}>
                      <button
                        onClick={() => pickContact(c)}
                        className={`w-full text-left px-3 py-2 hover:bg-violet-50 transition ${
                          sel ? "bg-violet-100" : ""
                        }`}
                        data-testid={`dropship-contact-${c.id}`}
                      >
                        <div className="text-sm font-semibold text-zinc-900">
                          {name}
                        </div>
                        <div className="text-[11px] text-zinc-500 truncate">
                          {c.address || c.email || "—"}
                          {c.city && `, ${c.city}`}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">
            {shipMode === "drop_ship" ? "Shipping address" : "Billing address"}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <FieldInput
            label="First name"
            value={address.first_name}
            onChange={(v) => setAddress((a) => ({ ...a, first_name: v }))}
            testId="addr-fname"
          />
          <FieldInput
            label="Last name"
            value={address.last_name}
            onChange={(v) => setAddress((a) => ({ ...a, last_name: v }))}
            testId="addr-lname"
          />
        </div>
        <FieldInput
          label="Address"
          value={address.address}
          onChange={(v) => setAddress((a) => ({ ...a, address: v }))}
          testId="addr-line1"
          placeholder="123 Main St"
        />
        <FieldInput
          label="Apt / Suite"
          value={address.address2}
          onChange={(v) => setAddress((a) => ({ ...a, address2: v }))}
          testId="addr-line2"
        />
        <div className="grid grid-cols-3 gap-2">
          <FieldInput
            label="City"
            value={address.city}
            onChange={(v) => setAddress((a) => ({ ...a, city: v }))}
            testId="addr-city"
          />
          <FieldInput
            label="State"
            value={address.state}
            onChange={(v) => setAddress((a) => ({ ...a, state: v }))}
            testId="addr-state"
          />
          <FieldInput
            label="ZIP"
            value={address.zip}
            onChange={(v) => setAddress((a) => ({ ...a, zip: v }))}
            testId="addr-zip"
          />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-zinc-600">Country</label>
          <select
            value={address.country || "US"}
            onChange={(e) => setAddress((a) => ({ ...a, country: e.target.value }))}
            className="h-10 w-full rounded-xl border border-zinc-200 px-3 text-sm bg-white outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
            data-testid="addr-country"
          >
            <option value="US">United States</option>
            <option value="CA">Canada</option>
          </select>
        </div>

        {shipMode === "ship_to_me" && (
          <>
            {/* Shipping address — collapsible, defaults to same as billing */}
            <div className="rounded-xl border border-zinc-200 mt-1">
              <button
                type="button"
                onClick={() => setShipOpen((o) => !o)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-zinc-700"
                data-testid="shipping-toggle"
              >
                <span>Shipping address</span>
                <ChevronDown
                  size={16}
                  className={`text-zinc-400 transition-transform ${shipOpen ? "rotate-180" : ""}`}
                />
              </button>
              {shipOpen && (
                <div className="px-3 pb-3 pt-3 space-y-2 border-t border-zinc-100">
                  <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sameAsBilling}
                      onChange={(e) => setSameAsBilling(e.target.checked)}
                      className="h-4 w-4 accent-emerald-600"
                      data-testid="shipping-same"
                    />
                    Same as billing address
                  </label>
                  {!sameAsBilling && (
                    <div className="space-y-2 pt-1">
                      <FieldInput
                        label="Shipping address"
                        value={shippingAddress.address}
                        onChange={(v) => setShippingAddress((b) => ({ ...b, address: v }))}
                        testId="ship-line1"
                        placeholder="123 Main St"
                      />
                      <FieldInput
                        label="Apt / Suite"
                        value={shippingAddress.address2}
                        onChange={(v) => setShippingAddress((b) => ({ ...b, address2: v }))}
                        testId="ship-line2"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <FieldInput
                          label="City"
                          value={shippingAddress.city}
                          onChange={(v) => setShippingAddress((b) => ({ ...b, city: v }))}
                          testId="ship-city"
                        />
                        <FieldInput
                          label="State"
                          value={shippingAddress.state}
                          onChange={(v) => setShippingAddress((b) => ({ ...b, state: v }))}
                          testId="ship-state"
                        />
                        <FieldInput
                          label="ZIP"
                          value={shippingAddress.zip}
                          onChange={(v) => setShippingAddress((b) => ({ ...b, zip: v }))}
                          testId="ship-zip"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-zinc-600">Country</label>
                        <select
                          value={shippingAddress.country || "US"}
                          onChange={(e) => setShippingAddress((b) => ({ ...b, country: e.target.value }))}
                          className="h-10 w-full rounded-xl border border-zinc-200 px-3 text-sm bg-white outline-none focus:border-emerald-400"
                          data-testid="ship-country"
                        >
                          <option value="US">United States</option>
                          <option value="CA">Canada</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={saveAddress}
              disabled={savingAddr}
              className="w-full h-10 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100 disabled:opacity-50 transition-colors"
              data-testid="save-address-btn"
            >
              {savingAddr ? "Saving…" : "Save address to my profile"}
            </button>
          </>
        )}
        <div className="grid grid-cols-2 gap-2">
          {shipMode === "drop_ship" ? (
            <div
              className="col-span-2 text-[11px] text-zinc-500 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
              data-testid="dropship-email-note"
            >
              No customer email is collected — the order confirmation goes only
              to you, not the customer.
            </div>
          ) : (
            <FieldInput
              label="Email"
              value={address.email}
              onChange={(v) => setAddress((a) => ({ ...a, email: v }))}
              testId="addr-email"
            />
          )}
          <FieldInput
            label="Phone"
            value={address.phone}
            onChange={(v) => setAddress((a) => ({ ...a, phone: v }))}
            testId="addr-phone"
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-semibold text-zinc-600" htmlFor="order-notes">
            Order notes <span className="font-normal text-zinc-400">(optional)</span>
          </label>
          <textarea
            id="order-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Anything we should know about this order?"
            className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 resize-none"
            data-testid="checkout-order-notes"
          />
        </div>

        <div className="flex gap-2 pt-1">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-xl"
            data-testid="checkout-back-to-cart"
          >
            <ArrowLeft size={13} className="mr-1" /> Cart
          </Button>
          <Button
            onClick={() => setStep(2)}
            disabled={!address.address || !address.city || !address.state || !address.zip}
            className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            data-testid="checkout-continue-to-payment"
          >
            Continue to Payment <ArrowRight size={13} className="ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  // ═══ STEP 2 — Payment ═════════════════════════════════════════════════
  if (step === 2) {
    // Square Scan-to-Pay (card / Apple Pay / Cash App, with the Visa/MC/Amex
    // marks) is the only card-checkout option, so it is shown to every buyer.
    const squareAllowed = true;
    const methods = [
      {
        k: "stripe_card",
        title: "Pay now (card)",
        sub: "Visa / Mastercard / Amex — secure checkout",
        icon: CreditCard,
        tint: "from-indigo-600 to-violet-700",
        brands: true,
      },
      // Scan to Pay (card / Apple Pay / Cash App via Square).
      ...(squareAllowed
        ? [
            {
              k: "square_scan",
              title: "Scan to Pay",
              sub: "QR · Card / Apple Pay / Cash App",
              icon: QrCode,
              tint: "from-violet-600 to-fuchsia-700",
              brands: true,
            },
          ]
        : []),
      {
        k: "zelle",
        title: "Zelle / Venmo",
        sub: "5% off · pay direct",
        icon: Banknote,
        tint: "from-amber-500 to-orange-600",
      },
    ];
    return (
      <div className="px-5 py-4 space-y-4" data-testid="checkout-step-2">
        {Steps}

        <h2 className="text-base font-bold text-zinc-900 flex items-center gap-2">
          <Banknote size={16} className="text-emerald-600" />
          {fullyCovered ? "Confirm your order" : "How do you want to pay?"}
        </h2>

        {rewardsAvailable > 0 && (
          <div
            className="rounded-2xl border border-amber-200 bg-amber-50/50 p-3 space-y-2"
            data-testid="rewards-box"
          >
            <label className="flex items-center justify-between gap-2 cursor-pointer">
              <span className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
                <Gift size={14} className="text-amber-600" />
                Use my rewards credit
              </span>
              <input
                type="checkbox"
                checked={useRewards}
                onChange={(e) => setUseRewards(e.target.checked)}
                className="h-4 w-4 accent-amber-600"
                data-testid="rewards-toggle"
              />
            </label>
            <div className="text-[11px] text-amber-700">
              Available balance: <b data-testid="rewards-available">{fmt(rewardsAvailable)}</b>
            </div>
            {useRewards && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-600">Apply</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={rewardsInput === "" ? rewardsApplied : rewardsInput}
                  onChange={(e) => setRewardsInput(e.target.value)}
                  className="w-28 h-9 rounded-lg border border-amber-200 px-2 text-sm"
                  data-testid="rewards-amount"
                />
                <button
                  type="button"
                  onClick={() =>
                    setRewardsInput(
                      String(Math.min(rewardsAvailable, payableBeforeRewards))
                    )
                  }
                  className="text-[11px] font-semibold text-amber-700 hover:underline"
                  data-testid="rewards-max"
                >
                  Max
                </button>
              </div>
            )}
          </div>
        )}

        {commAvailable > 0 && (
          <div
            className="rounded-2xl border border-violet-200 bg-violet-50/40 p-3 space-y-2"
            data-testid="commission-box"
          >
            <label className="flex items-center justify-between gap-2 cursor-pointer">
              <span className="text-sm font-semibold text-violet-900 flex items-center gap-1.5">
                <Banknote size={14} className="text-violet-600" />
                Use my commission
              </span>
              <input
                type="checkbox"
                checked={useCommission}
                onChange={(e) => setUseCommission(e.target.checked)}
                className="h-4 w-4 accent-violet-600"
                data-testid="commission-toggle"
              />
            </label>
            <div className="text-[11px] text-violet-700">
              Available balance: <b data-testid="commission-available">{fmt(commAvailable)}</b>
            </div>
            {useCommission && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-600">Apply</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={applyInput === "" ? commissionApplied : applyInput}
                  onChange={(e) => setApplyInput(e.target.value)}
                  className="w-28 h-9 rounded-lg border border-violet-200 px-2 text-sm"
                  data-testid="commission-amount"
                />
                <button
                  type="button"
                  onClick={() =>
                    setApplyInput(
                      String(Math.min(commAvailable, payableBeforeCommission))
                    )
                  }
                  className="text-[11px] font-semibold text-violet-700 hover:underline"
                  data-testid="commission-max"
                >
                  Max
                </button>
              </div>
            )}
          </div>
        )}

        {!fullyCovered && (
        <>
        <div className="grid grid-cols-2 gap-2">
          {methods.map((m) => {
            const Icon = m.icon;
            const active = method === m.k;
            return (
              <button
                key={m.k}
                onClick={() => setMethod(m.k)}
                className={`text-left rounded-2xl p-3 transition border ${
                  active
                    ? "border-emerald-500 ring-2 ring-emerald-300 shadow-sm"
                    : "border-zinc-200 hover:border-zinc-300"
                }`}
                data-testid={`pay-method-${m.k}`}
              >
                <div
                  className={`w-9 h-9 rounded-xl bg-gradient-to-br ${m.tint} text-white flex items-center justify-center mb-2 shadow`}
                >
                  <Icon size={16} />
                </div>
                <div className="text-sm font-bold text-zinc-900">{m.title}</div>
                <div className="text-[11px] text-zinc-500 leading-tight">
                  {m.sub}
                </div>
                {m.brands && <CardBrands />}
                {active && (
                  <div className="mt-1 text-[10px] text-emerald-700 font-semibold inline-flex items-center gap-0.5">
                    <CheckCircle2 size={10} /> Selected
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Method specifics */}
        {(method === "zelle" || method === "venmo") && (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-sm space-y-1">
            <div className="font-bold text-amber-900">5% off applied</div>
            <div className="text-amber-800">
              Send <b>{fmt(finalTotal)}</b> to{" "}
              <code className="bg-white px-1.5 py-0.5 rounded text-xs">
                {method === "zelle" ? ZELLE_HANDLE : VENMO_HANDLE}
              </code>{" "}
              with memo <b>#{cartTotal && "your order #"}</b>. We'll confirm
              once received.
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button
                onClick={() => setMethod("zelle")}
                className={`text-xs rounded-lg py-1.5 border ${method === "zelle" ? "border-amber-500 bg-white" : "border-zinc-200 bg-zinc-50"}`}
                data-testid="pay-zelle"
              >
                Zelle · {ZELLE_HANDLE}
              </button>
              <button
                onClick={() => setMethod("venmo")}
                className={`text-xs rounded-lg py-1.5 border ${method === "venmo" ? "border-amber-500 bg-white" : "border-zinc-200 bg-zinc-50"}`}
                data-testid="pay-venmo"
              >
                Venmo · {VENMO_HANDLE}
              </button>
            </div>
          </div>
        )}
        </>
        )}

        {/* Totals summary */}
        <div className="rounded-2xl bg-zinc-50 p-3 text-sm space-y-1.5">
          <div className="flex justify-between text-zinc-500">
            <span>Cart total</span>
            <span>{fmt(cartTotal)}</span>
          </div>
          {discountAmt > 0 && (
            <div className="flex justify-between text-emerald-700">
              <span>Discount (5%)</span>
              <span data-testid="discount-amt">−{fmt(discountAmt)}</span>
            </div>
          )}
          {rewardsApplied > 0 && (
            <div className="flex justify-between text-amber-700">
              <span>Rewards applied</span>
              <span data-testid="rewards-applied">−{fmt(rewardsApplied)}</span>
            </div>
          )}
          {commissionApplied > 0 && (
            <div className="flex justify-between text-violet-700">
              <span>Commission applied</span>
              <span data-testid="commission-applied">−{fmt(commissionApplied)}</span>
            </div>
          )}
          <div className="border-t border-zinc-200 pt-1.5 flex justify-between text-base">
            <span className="font-bold text-zinc-900">
              {fullyCovered ? "Amount due" : "Total to pay"}
            </span>
            <span
              className="font-bold text-emerald-700"
              data-testid="final-total"
            >
              {fmt(finalTotal)}
            </span>
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <Button
            variant="outline"
            onClick={() => setStep(1)}
            className="rounded-xl"
            data-testid="checkout-back-to-address"
          >
            <ArrowLeft size={13} className="mr-1" /> Address
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            data-testid="checkout-submit"
          >
            {busy ? (
              <Loader2 className="animate-spin mr-1" size={13} />
            ) : null}
            {fullyCovered
              ? "Place order · paid with credit"
              : method === "square_scan"
                ? "Generate QR code"
                : method === "stripe_card"
                  ? `Pay ${fmt(finalTotal)} by card`
                  : `Place order · ${fmt(finalTotal)}`}
          </Button>
        </div>
      </div>
    );
  }

  // ═══ STEP 3 — Done ════════════════════════════════════════════════════
  return (
    <div className="px-5 py-6 space-y-4" data-testid="checkout-step-3">
      {Steps}
      <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 text-center">
        <CheckCircle2
          size={42}
          className="mx-auto text-emerald-600 mb-2"
          strokeWidth={1.6}
        />
        <div className="text-lg font-bold text-zinc-900">
          Order #{orderResult?.order_number || "—"}
        </div>
        <div className="text-xs text-zinc-600 mt-1">
          {orderResult?.message || "Saved to your account."}
        </div>
      </div>

      {method === "square_scan" && squareUrl && (
        <div className="rounded-2xl border border-violet-200 bg-violet-50/30 p-4 text-center space-y-3">
          <div className="text-[11px] uppercase tracking-wide font-bold text-violet-700">
            Scan to Pay
          </div>
          <div className="bg-white rounded-xl p-4 inline-block">
            <QRCodeSVG value={squareUrl} size={220} includeMargin={false} />
          </div>
          <div className="text-xs text-zinc-600">
            Open the camera on your phone and point it at the code, or tap
            below to open on this device.
          </div>
          <a
            href={squareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold"
            data-testid="square-open-link"
          >
            <ExternalLink size={13} /> Open Square checkout
          </a>
        </div>
      )}

      {(method === "zelle" || method === "venmo") && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm space-y-1">
          <div className="font-bold text-amber-900">
            Pay {fmt(finalTotal)} via {method === "zelle" ? "Zelle" : "Venmo"}
          </div>
          <div className="text-amber-800">
            Send to{" "}
            <code className="bg-white px-1.5 py-0.5 rounded text-xs">
              {method === "zelle" ? ZELLE_HANDLE : VENMO_HANDLE}
            </code>
            , memo <b>#{orderResult?.order_number}</b>. We'll mark your order
            paid as soon as we see it.
          </div>
        </div>
      )}

      <Button
        onClick={onClose}
        className="w-full rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white"
        data-testid="checkout-close"
      >
        Done
      </Button>
    </div>
  );
}

function FieldInput({ label, value, onChange, testId, placeholder }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] text-zinc-500">{label}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-10 rounded-xl text-sm"
        data-testid={testId}
      />
    </div>
  );
}


// Visa / Mastercard / Amex brand marks shown on the Scan-to-Pay tile.
function CardBrands() {
  return (
    <div className="mt-1.5 flex items-center gap-1" data-testid="card-brands">
      <span className="inline-flex items-center justify-center h-4 px-1 rounded-[3px] bg-white border border-zinc-200 leading-none">
        <span className="text-[8px] font-extrabold italic tracking-tight text-[#1a1f71]">
          VISA
        </span>
      </span>
      <span className="inline-flex items-center justify-center h-4 w-6 rounded-[3px] bg-white border border-zinc-200">
        <svg viewBox="0 0 32 20" className="h-3" aria-label="Mastercard">
          <circle cx="12" cy="10" r="7" fill="#EB001B" />
          <circle cx="20" cy="10" r="7" fill="#F79E1B" fillOpacity="0.9" />
        </svg>
      </span>
      <span className="inline-flex items-center justify-center h-4 px-1 rounded-[3px] bg-[#2e77bc] leading-none">
        <span className="text-[7px] font-extrabold tracking-tight text-white">
          AMEX
        </span>
      </span>
    </div>
  );
}
